import { ref } from 'vue';

import { storeToRefs } from 'pinia';

import api from '@/api/index';
import { useAudioStore } from '@/stores/audio';
import { UseAudioPlayerOptions } from '@/types/composables';
import eventBus from '@/utils/eventBus';
import { eventKeys } from '@/utils/eventKeys';

const audioUnlocked = ref(false);

export function useAudioPlayer({ refAudioManual, refAudioAuto }: UseAudioPlayerOptions) {
  const audioStore = useAudioStore();
  const { audioInfoMap, audioDownloading } = storeToRefs(audioStore);

  const manualWaitingSrcs = ref<string[]>([]);
  const autoRetryCount = ref<number>(0);
  const playCallLogId = ref<string>('');
  const isPlayingAuto = ref(false);
  const isPlayingManual = ref(false);

  // 오디오 파일의 url 조회
  const fetchUrlByKey = async (key: string): Promise<string | null> => {
    try {
      const { success, data } = await api.get(`/api/chat/business/call/voice/${key}`);
      return success ? data : null;
    } catch (error) {
      console.error(`Fetch failed for chatId ${key}:`, error);
      return null;
    }
  };

  const checkPlayState = (url: URL, isPlay: boolean) => {
    const state = audioInfoMap.value.get(url.toString());
    if (!state) {
      return;
    }
    const isTarget = state.isTarget;
    playCallLogId.value = state.callLogId;

    if (isPlay) {
      audioStore.setPlayState(isTarget == '1' ? 2 : 1, playCallLogId.value);
    } else {
      playCallLogId.value = '';
      audioStore.setPlayState(0, playCallLogId.value);
    }
  };

  const unlockAutoPlay = async () => {
    const audioEl = refAudioAuto?.value;
    if (!audioEl) {
      console.warn('[F] refAudioManual is not available');
      return;
    }

    try {
      const silentAudioDataUrl = 'data:audio/wav;base64,UklGRiwAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
      audioEl.src = silentAudioDataUrl;
      audioEl.load(); // 무음 오디오 로드
      await audioEl.play();
      audioEl.pause();
      audioEl.currentTime = 0;
      audioEl.src = ''; // src를 비워두어 다음 오디오를 받을 준비

      console.log('[F] Audio autoplay unlocked using refAudioManual');
    } catch (err) {
      console.warn('[F] Unlock via refAudioManual failed:', err);
    }
  };

  // 오디오 수동재생
  const playAudioManual = async (keys: string[]) => {
    console.log(`start playAudioManual ${keys}`);
    const autoAudio = refAudioAuto.value;
    if (autoAudio && !autoAudio.paused) {
      // 자동재생 중일 경우 자동재생 정지
      refAudioAuto.value?.pause();
    }

    if (refAudioManual.value) refAudioManual.value.src = '';
    manualWaitingSrcs.value.length = 0;
    refAudioManual.value?.pause(); // 수동 재생중일 경우 기존 수동재생 정지

    for (let i = 0; i < keys.length; i++) {
      const pathAndQuery = keys[i].split('?');
      const audioKey = pathAndQuery[0];
      if (audioKey && audioKey.length > 1) {
        const url = await fetchUrlByKey(audioKey);

        if (url) {
          console.debug('FILE URL', url.toString());
          const params = new URLSearchParams(pathAndQuery[1]);
          audioInfoMap.value.set(url, {
            isTarget: params.get('isTarget') || '',
            callLogId: params.get('callLogId') || '',
          });
          audioDownloading.value = params.get('callLogId') || '';
          manualWaitingSrcs.value.push(url);

          if (i === 0) {
            refAudioManual.value!.src = url;
            refAudioManual.value!.volume = audioStore.playVolume;
            // setOutputDevice(refAudioManual.value!);
            refAudioManual.value!.load();
          }
        } else {
          console.error(`failed to fetch url: ${audioKey}`);
          return;
        }
      }
    }
  };

  // 오디오 수동재생 다음 파일
  const playNextManualAudio = async () => {
    if (manualWaitingSrcs.value && manualWaitingSrcs.value.length > 0) {
      manualWaitingSrcs.value.shift();
    }

    if (manualWaitingSrcs.value && manualWaitingSrcs.value.length > 0) {
      const url = manualWaitingSrcs.value[0];
      refAudioManual.value!.src = url;
      refAudioManual.value!.volume = audioStore.playVolume;
      // setOutputDevice(refAudioManual.value!);
      refAudioManual.value!.load();
    } else {
      if (refAudioManual.value?.src) {
        checkPlayState(new URL(refAudioManual.value.src), false);
        refAudioManual.value!.src = '';
      }
      playAudioAuto();
    }
  };

  // 오디오 자동재생
  const playAudioAuto = async () => {
    console.log('playAudioAuto first...');
    if (isPlayingAuto.value || isPlayingManual.value) return;
    console.log('playAudioAuto second...');
    isPlayingAuto.value = true;

    try {
      if (audioStore.playlistAutoQueue.length === 0) {
        isPlayingAuto.value = false;
        return;
      }

      if (audioStore.playState !== 0) {
        isPlayingAuto.value = false;
        return;
      }

      console.log('playAudioAuto third...');

      // 3번 재시도 실패 시 다음 오디오로 넘어가기
      if (autoRetryCount.value > 3) {
        audioStore.playlistAutoQueue.shift();
        autoRetryCount.value = 0;
        isPlayingAuto.value = false;
        return playAudioAuto();
      }

      const { src, idAudio, callLogId } = audioStore.playlistAutoQueue[0];
      console.log(`playAudioAuto play... ${src}`);
      const alreadyPlayed = audioStore.playlistAutoFinished.includes(idAudio);
      if (alreadyPlayed) {
        audioStore.playlistAutoQueue.shift();
        isPlayingAuto.value = false;
        return playAudioAuto();
      }

      audioStore.playlistAutoFinished.push(idAudio);

      if (refAudioAuto.value) {
        console.log(`playAudioAuto finally play... ${src}`);
        playCallLogId.value = callLogId;
        audioStore.setPlayState(2, playCallLogId.value);
        refAudioAuto.value.pause();
        refAudioAuto.value.src = src;
        autoRetryCount.value = 0;
        // setOutputDevice(refAudioAuto.value);
      }
    } finally {
      console.debug('finally');
    }
  };

  const shiftAutoAudio = () => {
    if (playCallLogId.value) {
      audioStore.setPlayState(0, playCallLogId.value);
      playCallLogId.value = '';
    }
    audioStore.playlistAutoQueue.shift();
  };

  // 자동, 수동재생 모두 일시정지
  const pauseAll = () => {
    if (refAudioManual.value) {
      refAudioManual.value.pause();
      refAudioManual.value.src = '';
    }

    if (refAudioAuto.value) {
      refAudioAuto.value.pause();
      refAudioAuto.value.src = '';
    }

    manualWaitingSrcs.value.length = 0;
    audioStore.playlistAutoQueue = [];
    audioStore.playState = 0;
    playCallLogId.value = '';
    isPlayingAuto.value = false;
    isPlayingManual.value = false;
  };

  eventBus.on(eventKeys.MIC_ON, pauseAll);

  return {
    playCallLogId,
    audioUnlocked,
    refAudioManual,
    manualWaitingSrcs,
    refAudioAuto,
    autoRetryCount,
    isPlayingAuto,
    isPlayingManual,
    playAudioManual,
    playNextManualAudio,
    playAudioAuto,
    shiftAutoAudio,
    checkPlayState,
    fetchUrlByKey,
    unlockAutoPlay,
  };
}
